import type { ReactNode } from 'react';
import {
  Controller,
  useWatch,
  type Control,
  type FieldErrors,
  type UseFormRegister,
  type UseFormSetValue,
} from 'react-hook-form';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { TextField } from '@/components/forms/TextField';
import { AvatarUploadField } from '@/components/common/AvatarUploadField';
import { initialsFromName } from '@/lib/utils/user.utils';
import { INVESTOR_FIRM_LOGO_LABEL } from './investor-form.constants';
import { SelectField } from '@/components/forms/SelectField';
import { TextareaField } from '@/components/forms/TextareaField';
import { PhoneField } from '@/components/forms/PhoneField';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { CONTACT_TITLE_OPTIONS } from '@/lib/constants/contact-titles';
import type { InvestorProfileData } from '../../api/investor-profile.api';
import type { InvestorProfileFormValues } from './schema';
import { INVESTOR_TYPE_OPTIONS } from './investor-options';

const S = UI_STRINGS.INVESTOR_PROFILE;

function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <rect x="2" y="5" width="8" height="6" rx="1" stroke="currentColor" strokeWidth="1.2" />
      <path d="M4 5V3.5a2 2 0 0 1 4 0V5" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function ExternalLinkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
        d="M5.5 3.5h-2A1.5 1.5 0 0 0 2 5v5.5A1.5 1.5 0 0 0 3.5 12H9a1.5 1.5 0 0 0 1.5-1.5v-2M8.5 2H12v3.5M12 2L6.5 7.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The phone picker plus a way to remove a phone saved as free text that cannot be read as
 * a number: it shows beside the empty field, and without this nothing could ever delete it.
 */
function PhoneWithLegacy({
  control,
  errors,
  defaultCountryIso2,
}: {
  control: Control<InvestorProfileFormValues>;
  errors?: FieldErrors<InvestorProfileFormValues>;
  defaultCountryIso2?: string;
}) {
  const phone = useWatch({ control, name: 'phone' });
  const legacyPhone = useWatch({ control, name: 'legacyPhone' });
  return (
    <div className="flex flex-col gap-1">
      <Controller
        control={control}
        name="phone"
        render={({ field }) => (
          <PhoneField
            {...field}
            label={S.PHONE}
            legacyText={legacyPhone}
            defaultCountryIso2={defaultCountryIso2}
            error={errors?.phone?.message}
          />
        )}
      />
      {legacyPhone && !phone && (
        <Controller
          control={control}
          name="legacyPhone"
          render={({ field }) => (
            <Button variant="link-sm" className="self-start" onClick={() => field.onChange('')}>
              {S.REMOVE_SAVED_PHONE}
            </Button>
          )}
        />
      )}
    </div>
  );
}

interface IdentitySectionProps {
  data: InvestorProfileData;
  control: Control<InvestorProfileFormValues>;
  register: UseFormRegister<InvestorProfileFormValues>;
  errors?: FieldErrors<InvestorProfileFormValues>;
  setValue?: UseFormSetValue<InvestorProfileFormValues>;
  onLogoUploadingChange?: (isUploading: boolean) => void;
  /** The contact photo upload, shown beside the firm logo so the two images share one row. */
  photoSlot?: ReactNode;
  /** `iso2` of the person's own country, where the phone picker starts. */
  defaultPhoneCountryIso2?: string;
}

/**
 * The About panel's fields: firm (read-only), fund, contact, website and bio
 * (US-040 AC2, FR-187). The read view is `InvestorAboutCard` (MYN-217).
 */
export function IdentitySection({
  data,
  control,
  register,
  errors,
  setValue,
  onLogoUploadingChange,
  photoSlot,
  defaultPhoneCountryIso2,
}: IdentitySectionProps) {
  return (
    <Card title={S.IDENTITY} headerDivider hoverEffect={false}>
      <div className="flex flex-col gap-4">
        {/* The FIRM's mark, distinct from the contact headshot in the header
            card above - separate fields on separate S3 prefixes (MYN-175). The
            backend purpose, DTO field and read resolution all shipped with that
            ticket; this control was the missing half, so the logo could never
            be set. Preview reads the RESOLVED `data.logoUrl`; the upload writes
            the raw key to `logoFileKey`. */}
        <div className="grid gap-4 sm:grid-cols-2">
          {photoSlot}
          {setValue && (
            <div className="flex flex-col gap-1">
              <FieldLabel label={INVESTOR_FIRM_LOGO_LABEL} variant="caps" />
              <AvatarUploadField
                photoUrl={data.logoUrl ?? undefined}
                initials={initialsFromName(data.firmName || 'Fund')}
                uploadKind="investorLogo"
                accept="image/png,image/jpeg"
                uploadCaption={S.UPLOAD_HINT}
                tooLargeMessage={S.PHOTO_TOO_LARGE}
                errorMessage={S.PHOTO_UPLOAD_ERROR}
                onUploaded={(result) =>
                  setValue('logoFileKey', result.fileKey, { shouldDirty: true })
                }
                onUploadingChange={onLogoUploadingChange}
              />
            </div>
          )}
        </div>
        {/* The firm is the entity name the person was invited under: shown, never edited here (FR-187). */}
        {data.entityName && (
          <div className="flex flex-col gap-1">
            <FieldLabel label={S.FIRM_NAME} variant="caps" />
            <p className="text-input text-navy font-medium">{data.entityName}</p>
          </div>
        )}
        {/* Short values two to a row; the bio keeps the full width. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            {...register('firmName')}
            label={S.FUND_NAME}
            labelVariant="caps"
            error={errors?.firmName?.message}
          />
          <SelectField
            {...register('investorType')}
            label={S.INVESTOR_TYPE}
            labelVariant="caps"
            options={INVESTOR_TYPE_OPTIONS}
            placeholder="Select a type"
            error={errors?.investorType?.message}
          />
          <TextField
            {...register('contactName')}
            label={S.CONTACT_NAME}
            labelVariant="caps"
            error={errors?.contactName?.message}
          />
          <SelectField
            {...register('contactTitle')}
            label={S.CONTACT_TITLE}
            labelVariant="caps"
            options={CONTACT_TITLE_OPTIONS}
            placeholder="Select a title"
            error={errors?.contactTitle?.message}
          />
          <div className="flex flex-col gap-1.5">
            <FieldLabel label={S.EMAIL} variant="caps" />
            <Input
              label={S.EMAIL}
              value={data.email ?? ''}
              disabled
              readOnly
              onChange={() => {}}
              rightIcon={<LockIcon />}
              className="bg-page-bg"
            />
            <p className="text-p6 text-slate pl-1 italic">{S.EMAIL_LOCKED_HINT}</p>
          </div>
          <PhoneWithLegacy
            control={control}
            errors={errors}
            defaultCountryIso2={defaultPhoneCountryIso2}
          />
          <TextField
            {...register('websiteUrl')}
            label={S.WEBSITE}
            labelVariant="caps"
            type="url"
            rightIcon={<ExternalLinkIcon />}
            error={errors?.websiteUrl?.message}
          />
          <TextField
            {...register('linkedinUrl')}
            label={S.LINKEDIN}
            labelVariant="caps"
            type="url"
            rightIcon={<ExternalLinkIcon />}
            error={errors?.linkedinUrl?.message}
          />
        </div>
        <TextareaField
          {...register('intro')}
          label={S.BIO}
          labelVariant="caps"
          rows={4}
          error={errors?.intro?.message}
        />
      </div>
    </Card>
  );
}
